import {
  MARKETING_CONSENT_EVENT,
  readMarketingConsent,
} from "@/components/analytics/consent";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

const META_PIXEL_ID = "28586648397618081";

type MetaPixelFunction = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  loaded: boolean;
  push: (...args: unknown[]) => void;
  version: string;
};

declare global {
  interface Window {
    _fbq?: MetaPixelFunction;
    fbq?: MetaPixelFunction;
  }
}

function initializeMetaPixel() {
  if (window.fbq) return;

  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
      return;
    }
    fbq.queue.push(args);
  }) as MetaPixelFunction;

  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.push = (...args: unknown[]) => fbq(...args);

  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  fbq("init", META_PIXEL_ID);
}

export function MetaPixel() {
  const location = useLocation();
  const [enabled, setEnabled] = useState(false);
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    const sync = () => setEnabled(readMarketingConsent() === "granted");
    sync();
    window.addEventListener(MARKETING_CONSENT_EVENT, sync);
    return () => window.removeEventListener(MARKETING_CONSENT_EVENT, sync);
  }, []);

  useEffect(() => {
    if (!enabled || import.meta.env.DEV) return;
    initializeMetaPixel();
  }, [enabled]);

  useEffect(() => {
    if (!enabled || import.meta.env.DEV || !window.fbq) return;

    const path = `${location.pathname}${location.searchStr}${location.hash}`;
    if (previousPath.current === path) return;

    previousPath.current = path;
    window.fbq("track", "PageView");
  }, [enabled, location.hash, location.pathname, location.searchStr]);

  return null;
}
